<template>
  <div
    v-clickOutside="save"
    class="relative flex flex-col justify-between rounded-8 p-14 h-full w-full mb-20 min-h-94"
    :class="isDarkMode ? 'border-2 border-grey-500' : 'shadow-event'"
  >
    <EmojiPicker
      v-if="showEmojiPicker"
      ref="emojiPickerRef"
      v-clickOutside="closeEmojiPicker"
      class="top-80 left-0"
      :class="{ '-top-280': isMobile }"
      @selectEmoji="insertEmoji"
      @click.stop
    />

    <div>
      <input
        id="title"
        ref="titleInputRef"
        v-model="event.title"
        placeholder="What’s the event?"
        class="font-Inter w-full mb-10 text-15 bg-transparent outline-none font-bold pr-5 leading-small block"
        maxlength="40"
        type="text"
        @keypress="onKeyPress"
        @keypress.enter="save"
      >

      <input
        id="subTitle"
        ref="subTitleInputRef"
        v-model="event.subTitle"
        placeholder="Describe it in more detail."
        class="w-full h-full text-14 bg-transparent outline-none -mt-5 block"
        maxlength="100"
        type="text"
        @keypress="onKeyPress"
        @keypress.enter="save"
      >

      <div class="border-t border-grey-500 mt-5" />

      <div class="flex items-center justify-between mb-10 mt-5">
        <Mentions v-model="event.project" class="mr-5" />
        <SelectedBookModal
          max-width="max-w-150"
          :books="allBooks"
          :selected-book="selectedBook"
          label-font-size="text-13 py-1"
          label-classes="mr-8 text-12 w-18 h-18"
          @toggleBook="toggleBook"
        />
      </div>
    </div>

    <div class="flex justify-between items-center text-12 mt-5">
      <div class="text-14">
        <div class="leading-none cursor-pointer flex items-center mb-3">
          <i class="icon-calendar mr-5" />
          <p class="font-bold flex-shrink-0">{{ computedDay.date }} {{ computedDay.month }}</p>
        </div>
        <TimeInput :time="time" @setTime="setTime" />
      </div>

      <div class="flex items-center">
        <Recurring
          :is-edit="true"
          :period-type="event.periodType"
          :period-value="event.periodValue"
          :is-new-event="true"
          @addRecurring="addRecurring"
          @updateRecurring="updateRecurring"
          @deleteRecurring="deleteRecurring"
        />
        <TagsModal :selected-tags="event.eventTags" :tags="tags" @toggleTag="toggleEventTag" @updateTag="updateTag" />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref, onMounted } from 'vue'
import { useStore } from 'vuex'

import TagsModal from '@/components/week-planner/TagsModal'
import Mentions from '@/components/week-planner/Mentions'
import Recurring from '@/components/week-planner/Recurring'
import TimeInput from '@/components/week-planner/TimeInput'
import EmojiPicker from '@/components/shared/EmojiPicker'
import SelectedBookModal from '@/components/task-board/SelectedBookModal'
// TODO: move the SelectedBookModal component to the week-planner folder

export default defineComponent({
  components: { TagsModal, Mentions, Recurring, TimeInput, EmojiPicker, SelectedBookModal },

  props: {
    startDate: String,
    lastWeekDate: String,
    firstWeekDate: String,
    selectedBooks: { type: Array, default: () => [] }
  },

  emits: ['focused', 'hideNewEvent'],

  setup (props, { emit }) {
    const { state, commit, getters, dispatch } = useStore()

    const titleInputRef = ref(null)
    const subTitleInputRef = ref(null)

    const showEmojiPicker = ref(false)

    onMounted(() => {
      titleInputRef.value.focus()
    })

    const selectedBook = ref(props.selectedBooks[0])

    const books = computed(() => state.book.books.filter(b => !b.isSection))
    const headquarters = computed(() => state.team.headquarters)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const tags = computed(() => state.planner.tags.filter(tag => tag.bookId === selectedBook.value?.id))
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isTwilightTheme = computed(() => getters.isTwilightTheme)

    const allBooks = computed(() => {
      const result = books.value
      if (isTeamAccount.value) {
        result.push(headquarters.value)
      }
      return result
    })

    const time = ref('')

    const event = ref({
      title: '',
      subTitle: '',
      project: null,
      eventTags: [],
      periodType: null,
      periodValue: null,
      timeStart: null,
      timeEnd: null
    })

    const computedDay = computed(() => {
      const date = new Date(props.startDate).toLocaleString('en-US', { day: '2-digit' })
      const month = new Date(props.startDate).toLocaleString('en-US', { month: 'long' })

      return { date, month }
    })

    async function save () {
      if (event.value.title.trim() === '') {
        emit('hideNewEvent')
        return
      }

      commit('toggleBookLoading', true)

      const { data } = await dispatch('createEvent', {
        title: event.value.title,
        subTitle: event.value.subTitle,
        startDate: props.startDate,
        timeStart: event.value.timeStart,
        timeEnd: event.value.timeEnd,
        periodType: event.value.periodType || 'none',
        periodValue: event.value.periodValue || 0,
        projectId: event.value.project?.id,
        tagIds: event.value.eventTags.map(item => item.id),
        bookId: selectedBook.value.id
      })

      if (event.value.periodType && event.value.periodValue) {
        await dispatch('addRecurring', {
          startDate: data.showAt,
          eventId: data.eventId,
          periodType: event.value.periodType,
          periodValue: event.value.periodValue,
          lastWeekDate: props.lastWeekDate
        })
      }

      emit('hideNewEvent')

      const events = state.planner.events
      const allEvents = state.planner.allEvents
      events.push(data)
      allEvents.push(data)

      commit('setEvents', events)
      commit('setAllEvents', allEvents)
      commit('toggleBookLoading', false)
    }

    function setTime ({ timeStart, timeEnd }) {
      event.value.timeStart = timeStart
      event.value.timeEnd = timeEnd
    }

    function toggleEventTag ({ tag, isSelected }) {
      if (isSelected) {
        event.value.eventTags = event.value.eventTags.filter(t => t.id !== tag.id)
      } else {
        event.value.eventTags.push(tag)
      }
    }

    function updateTag ({ name, id }) {
      dispatch('updateEventTag', { name, id, bookId: selectedBook.value.id })
        .then(() => {
          dispatch('getEventsByDate', {
            startDate: props.firstWeekDate,
            finishDate: props.lastWeekDate,
            bookIds: props.selectedBooks.map(b => b.id)
          }).finally(_ => commit('toggleBookLoading', false))

          dispatch('getUpcomingEvents', {
            startDate: props.lastWeekDate,
            limit: 50,
            bookIds: props.selectedBooks.map(b => b.id)
          })
        })
    }

    function addRecurring (period) {
      event.value.periodType = period.type
      event.value.periodValue = period.value
    }

    function updateRecurring ({ period }) {
      event.value.periodType = period.type
      event.value.periodValue = period.value
    }

    function deleteRecurring () {
      event.value.periodType = null
      event.value.periodValue = null
    }

    let focusedInput = ''

    function closeEmojiPicker () {
      showEmojiPicker.value = false
    }

    function onKeyPress (e) {
      if (e.keyCode === 58) {
        focusedInput = e.target.id
        showEmojiPicker.value = true
      } else {
        focusedInput = ''
        showEmojiPicker.value = false
      }
    }

    function insertEmoji (emoji) {
      if (focusedInput === 'title') {
        titleInputRef.value.focus()
        if (event.value.title.length >= 40) return
      } else {
        subTitleInputRef.value.focus()
        if (event.value.subTitle.length >= 100) return
      }

      if (event.value[focusedInput].slice(-1) === ':') {
        event.value[focusedInput] = event.value[focusedInput].slice(0, -1) + emoji.char
      } else {
        event.value[focusedInput] += emoji.char
      }
    }

    function toggleBook (book) {
      event.value.eventTags = []
      selectedBook.value = book
    }

    return {
      // refs
      titleInputRef,
      subTitleInputRef,
      // data
      time,
      tags,
      event,
      showEmojiPicker,
      // computed
      allBooks,
      isMobile,
      isDarkMode,
      computedDay,
      selectedBook,
      isTwilightTheme,
      // function
      save,
      setTime,
      toggleEventTag,
      updateTag,
      toggleBook,
      onKeyPress,
      insertEmoji,
      addRecurring,
      deleteRecurring,
      updateRecurring,
      closeEmojiPicker
    }
  }
})
</script>
